<template>
  <div id="app" class="font-sans antialiased text-gray-800">
    <div class="flex h-screen min-h-screen overflow-hidden">
      <aside class="sidebar flex-shrink-0 w-full min-h-full" v-if="!collapsed">
        <div class="flex flex-col h-full">
          <div class="flex items-center px-5 pt-6 pb-5">
            <div class="brand-logo flex items-center justify-center flex-shrink-0">
              <img
                class="w-10 h-10 rounded-full"
                src="@/assets/images/Logo_white.png"
                alt="Taxiye"
              />
            </div>
            <div class="ml-3 overflow-hidden">
              <p class="text-lg font-bold leading-tight tracking-wide text-gray-900">Taxiye</p>
              <span
                v-if="corporateRole ? currentCorporateRole.roleName : currentRole.roleName"
                class="role-badge inline-block max-w-full px-2 mt-1 text-xs font-semibold tracking-wider uppercase truncate rounded-full"
              >{{ corporateRole ? currentCorporateRole.roleName : currentRole.roleName }}</span>
            </div>
          </div>

          <div class="px-4 pb-2">
            <div class="relative">
              <input
                type="text"
                class="sidebar-search block w-full py-2 pl-10 pr-9 text-sm rounded-lg focus:outline-none"
                placeholder="Search menu..."
                v-model="filter"
              />
              <div class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <span class="search-icon text-lg material-icons-round">search</span>
              </div>
              <button
                v-if="filter"
                @click="filter = ''"
                type="button"
                title="Clear search"
                class="search-clear absolute inset-y-0 right-0 flex items-center pr-3 focus:outline-none"
              >
                <span class="text-base material-icons-round">close</span>
              </button>
            </div>
          </div>

          <nav v-if="hasNavs" class="flex-grow px-3 pt-3 pb-4 overflow-y-auto sidebar-scroll">
            <div v-for="group in sidebarGroups" :key="group.name" class="mb-5">
              <p class="side-group-label px-3 pb-2 text-xs font-semibold tracking-widest uppercase">{{ group.name }}</p>
              <ul class="space-y-1 text-sm font-medium">
                <li v-for="nav in group.items" :key="nav.url">
                  <router-link
                    class="side-link relative flex items-center px-3 py-2 rounded-lg"
                    :class="{ 'side-link--active': $route.path === nav.url }"
                    :to="nav.url"
                  >
                    <span
                      v-if="$route.path === nav.url"
                      class="side-link-indicator absolute inset-y-0 left-0 w-1 h-5 my-auto -ml-3 rounded-r-full"
                    ></span>
                    <i class="side-link-icon mr-3 text-xl material-icons-round">{{ nav.icon }}</i>
                    <span class="truncate">{{ nav.title }}</span>
                  </router-link>
                </li>
              </ul>
            </div>
          </nav>
          <div v-else class="sidebar-empty flex flex-col items-center flex-grow px-6 py-10 text-center">
            <span class="mb-2 text-4xl material-icons-round">{{ filter ? "search_off" : "inbox" }}</span>
            <p class="text-sm">{{ filter ? "No modules found matching your search" : "No modules found" }}</p>
          </div>

          <div v-if="date" class="sidebar-footer flex items-center px-5 py-3">
            <span class="mr-2 text-base material-icons-round">schedule</span>
            <p class="text-xs font-medium tracking-wide truncate">Server time{{ date }}</p>
          </div>
        </div>
      </aside>

      <div class="flex flex-col flex-1 overflow-y-auto bg-white">
        <div class="z-30 px-6 py-3 bg-white border-b border-gray-200">
          <div class="flex items-center justify-between">
            <div class="flex items-center space-x-4">
              <button
                class="flex items-center justify-center w-10 h-10 text-gray-500 rounded-lg transition-colors duration-150 hover:bg-gray-100 hover:text-gray-900 focus:outline-none"
                @click="collapsed = !collapsed"
              >
                <span class="material-icons-round">
                  {{
                    collapsed ? "menu" : "menu_open"
                  }}
                </span>
              </button>
              <img
                v-if="collapsed"
                class="w-auto h-10"
                src="@/assets/images/Logo_white.png"
                alt="Taxiye"
              />
            </div>

            <div class="items-center hidden text-sm font-medium text-gray-400 capitalize md:flex">
              <div v-for="(path, index) in paths" :key="path.path" class="flex items-center">
                <span
                  v-if="index"
                  class="mx-1 text-base text-gray-300 material-icons-round"
                >chevron_right</span>
                <span v-if="path.current" class="font-semibold text-gray-800">{{ path.text || path.path }}</span>
                <router-link
                  v-else
                  :to="path.to"
                  class="transition-colors duration-150 hover:text-gray-800"
                >{{ path.text || path.path }}</router-link>
              </div>
            </div>
            <div
              @click="dropdown = !dropdown"
              class="relative flex items-center px-2 py-1 space-x-3 rounded-lg cursor-pointer select-none transition-colors duration-150 hover:bg-gray-100"
            >
              <div class="flex flex-col items-end">
                <div class="text-sm font-semibold text-gray-800 capitalize">{{ name }}</div>
                <div
                  v-if="$store.getters.corporate"
                  class="text-xs text-gray-400"
                >{{ $store.getters.corporate }}</div>
              </div>
              <div
                class="flex items-center justify-center w-10 h-10 text-sm font-bold rounded-full bg-taxiye-100 text-taxiye-500"
              >{{ initials }}</div>
              <span class="text-gray-400 material-icons-round">expand_more</span>
              <transition name="fade">
                <div
                  v-if="dropdown"
                  class="absolute top-0 right-0 z-40 w-56 mt-12 overflow-hidden bg-white border border-gray-100 rounded-lg shadow-lg"
                >
                  <button
                    v-if="roles.length > 0 || corporateRoles.length > 0"
                    @click.stop="changeRole"
                    type="button"
                    class="flex items-center w-full px-4 py-3 text-sm text-left text-gray-600 outline-none transition-colors duration-150 hover:bg-gray-50 active:outline-none"
                  >
                    <span class="mr-3 text-lg text-gray-400 material-icons-round">swap_horiz</span>
                    Change Role
                  </button>
                  <button
                    @click.stop="logout"
                    type="button"
                    class="flex items-center w-full px-4 py-3 text-sm text-left text-red-500 border-t border-gray-100 outline-none transition-colors duration-150 hover:bg-red-50 active:outline-none"
                  >
                    <span class="mr-3 text-lg material-icons-round">logout</span>
                    Logout
                  </button>
                </div>
              </transition>
            </div>
          </div>
        </div>
        <div class="flex-1">
          <router-view />
        </div>
      </div>
    </div>

    <portal-target name="modals"></portal-target>
  </div>
</template>

<script>
// import { ROLES } from "@/config";

// Sidebar sections, keyed by nav moduleName. Order here is display order;
// groups with no accessible modules are hidden.
const NAV_GROUPS = {
  Overview: ["dashboard", "birds-eye-view", "corporate-dashboard"],
  Trips: [
    "trips",
    "trip-searches",
    "trip-requests",
    "coupon",
    "rents",
    "manual-trip-booking",
    "corporate-trips",
    "corporate-trip-searches",
    "corporate-trip-requests",
  ],
  Operations: ["tickets", "employees", "corporate-dispatcher"],
  People: [
    "passengers",
    "drivers",
    "drivers-leaderboard",
    "passengers-leaderboard",
  ],
  Fleet: ["vehicle-type", "vehicles"],
  Finance: [
    "finance",
    "wallet-managment",
    "second-wallet-management",
    "loan-history",
  ],
  Rewards: [
    "incentives",
    "reward-history",
    "reward-packages",
    "reward-inventory",
    "promo-history",
  ],
  Safety: ["sos", "sos-history", "mock-reports", "device-bans"],
  Corporate: ["corporate-management", "corporate-reports"],
  Reports: ["reports"],
  Administration: [
    "users",
    "rbac",
    "push-notification",
    "localization",
    "site-setting",
    "activity-logs",
    "logs",
  ],
};

export default {
  data() {
    return {
      dropdown: false,
      collapsed: false,
      filter: "",
      allNavs: [
        {
          title: "Dashboard",
          url: "/dashboard",
          icon: "dashboard",
          moduleName: "dashboard",
          // roles: [ROLES.ADMIN, ROLES.OPERATION],
        },
        {
          title: "Users",
          url: "/accounts",
          icon: "manage_accounts",
          // roles: [ROLES.ADMIN],
          moduleName: "users",
        },
        {
          title: "Corporate Management",
          url: "/corporate",
          icon: "domain",
          // roles: [ROLES.ADMIN],
          moduleName: "corporate-management",
        },
        {
          title: "Passengers",
          url: "/passengers",
          icon: "directions_walk",
          // roles: [ROLES.ADMIN, ROLES.DISPATCHER, ROLES.OPERATION],
          moduleName: "passengers",
        },
        {
          title: "Incentives",
          url: "/incentives",
          icon: "volunteer_activism",
          // roles: [ROLES.ADMIN, ROLES.FINANCE, ROLES.OPERATION],
          moduleName: "incentives",
        },
        {
          title: "Drivers",
          url: "/drivers",
          icon: "people_alt",
          // roles: [ROLES.ADMIN, ROLES.DISPATCHER, ROLES.OPERATION],
          moduleName: "drivers",

        },
        // {
        //   title: "Drivers Finance",
        //   url: "/drivers-finance",
        //   icon: "people_alt",
        //   // roles: [ROLES.ADMIN, ROLES.DISPATCHER, ROLES.OPERATION],
        //   moduleName: "drivers-finance",

        // },
        {
          title: "RBAC",
          url: "/rbac",
          icon: "security",
          // roles: [ROLES.ADMIN],
          moduleName: "rbac",

        },
        {
          title: "Reports",
          url: "/reports",
          icon: "summarize",
          // roles: [ROLES.ADMIN],

          moduleName: "reports",
        },
        {
          title: "Corporate Reports",
          url: "/corporate-reports",
          icon: "summarize",
          // roles: [ROLES.ADMIN],

          moduleName: "corporate-reports",
        },
        {
          title: "Trips",
          url: "/rides",
          icon: "electric_car",
          // roles: [ROLES.ADMIN, ROLES.DISPATCHER, ROLES.OPERATION],
          moduleName: "trips",

        },
        {
          title: "Trips Searches (Passengers)",
          url: "/trip-searches",
          icon: "hail",
          // roles: [ROLES.ADMIN, ROLES.DISPATCHER, ROLES.OPERATION],
          moduleName: "trip-searches",

        },
        {
          title: "Trip Requests (Drivers)",
          url: "/trip-requests",
          icon: "swipe",
          // roles: [ROLES.ADMIN, ROLES.DISPATCHER, ROLES.OPERATION],
          moduleName: "trip-requests",

        },
        {
          title: "Trip Coupons",
          url: "/coupons",
          icon: "confirmation_number",
          // roles: [ROLES.ADMIN, ROLES.DISPATCHER, ROLES.OPERATION],
          moduleName: "coupon",

        },
        {
          title: "Rents",
          url: "/rents",
          icon: "car_rental",
          // roles: [ROLES.ADMIN, ROLES.DISPATCHER, ROLES.OPERATION],
          moduleName: "rents",

        },
        {
          title: "Reward History",
          url: "/rewards",
          icon: "emoji_events",
          // roles: [ROLES.ADMIN, ROLES.FINANCE, ROLES.OPERATION],
          moduleName: "reward-history",

        },
        {
          title: "Reward Packages",
          url: "/reward-packages",
          icon: "card_giftcard",
          // roles: [ROLES.ADMIN],
          moduleName: "reward-packages",

        },
        {
          title: "Reward Inventory",
          url: "/rewards-inventory",
          icon: "inventory_2",
          // roles: [ROLES.ADMIN],
          moduleName: "reward-inventory",

        },
        {
          title: "Manual Trip Booking",
          url: "/manual-trip-booking",
          icon: "edit_road",
          // roles: [ROLES.ADMIN, ROLES.DISPATCHER, ROLES.OPERATION],
          moduleName: "manual-trip-booking",

        },
        {
          title: "Finance",
          url: "/finance",
          icon: "calculate",
          // roles: [ROLES.ADMIN, ROLES.FINANCE, ROLES.OPERATION],
          moduleName: "finance",

        },
        {
          title: "Wallet Managment",
          url: "/wallet-managment",
          icon: "account_balance_wallet",
          // roles: [ROLES.ADMIN, ROLES.DISPATCHER, ROLES.FINANCE, ROLES.OPERATION],
          moduleName: "wallet-managment",

        },
        {
          title: "Second Wallet Managment",
          url: "/second-wallet-management",
          icon: "savings",
          // roles: [ROLES.ADMIN, ROLES.DISPATCHER, ROLES.FINANCE, ROLES.OPERATION],
          moduleName: "second-wallet-management",
        },
        {
          title: "Loan History",
          url: "/loan-history",
          icon: "request_quote",
          // roles: [ROLES.ADMIN, ROLES.OPERATION],
          moduleName: "loan-history",

        },
        {
          title: "Vehicle Type",
          url: "/vehicle-types",
          icon: "commute",
          // roles: [ROLES.ADMIN],
          moduleName: "vehicle-type",

        },
        {
          title: "Vehicles",
          url: "/vehicles",
          icon: "directions_car",
          // roles: [ROLES.ADMIN, ROLES.DISPATCHER, ROLES.OPERATION],
          moduleName: "vehicles",

        },
        {
          title: "Promo History",
          url: "/promos",
          icon: "emoji_events",
          // roles: [ROLES.ADMIN, ROLES.FINANCE, ROLES.OPERATION],
          moduleName: "promo-history",

        },
        {
          title: "Device Bans",
          url: "/device-bans",
          icon: "block",
          // roles: [ROLES.ADMIN, ROLES.OPERATION],

          moduleName: "device-bans",
        },
        {
          title: "Driver Bans",
          url: "/driver-bans",
          icon: "person_off",
          // roles: [ROLES.ADMIN, ROLES.OPERATION],

          moduleName: "device-bans",
        },
        {
          title: "Drivers Leaderboard",
          url: "/driver-leaderboard",
          icon: "leaderboard",
          // roles: [ROLES.ADMIN, ROLES.OPERATION],
          moduleName: "drivers-leaderboard",

        },
        {
          title: "Passengers Leaderboard",
          url: "/passenger-leaderboard",
          icon: "leaderboard",
          // roles: [ROLES.ADMIN, ROLES.OPERATION],
          moduleName: "passengers-leaderboard",

        },
        {
          title: "Mock Reports",
          url: "/mock-reports",
          icon: "warning",
          // roles: [ROLES.ADMIN, ROLES.DISPATCHER, ROLES.OPERATION],

          moduleName: "mock-reports",
        },
        // {
        //   title: "Payment Records",
        //   url: "/payment-records",
        //   icon: "money",
        //   // roles: [ROLES.ADMIN, ROLES.FINANCE],
        // },
        // {
        //   title: "Reviews & Ratings",
        //   url: "/reviews-and-ratings",
        //   icon: "stars",
        //   // roles: [ROLES.ADMIN],
        // },
        {
          title: "Bird's-Eye View",
          url: "/birds-eye-view",
          icon: "gps_fixed",
          // roles: [ROLES.ADMIN, ROLES.DISPATCHER, ROLES.OPERATION],
          moduleName: "birds-eye-view",

        },
        {
          title: "SOS",
          url: "/sos",
          icon: "announcement",
          // roles: [ROLES.ADMIN, ROLES.DISPATCHER, ROLES.OPERATION],
          moduleName: "sos",

        },
        {
          title: "SOSHistory",
          url: "/sos-history",
          icon: "history",
          // roles: [ROLES.ADMIN, ROLES.DISPATCHER, ROLES.OPERATION],

          moduleName: "sos-history",
        },
        {
          title: "Push Notification",
          url: "/push-notification",
          icon: "notifications_active",
          // roles: [ROLES.ADMIN, ROLES.OPERATION],
          moduleName: "push-notification",

        },
        {
          title: "Localizations",
          url: "/Localizations",
          icon: "language",
          // roles: [ROLES.ADMIN, ROLES.OPERATION],
          moduleName: "localization"
        },
        {
          title: "Site Setting",
          url: "/setting",
          icon: "settings",
          // roles: [ROLES.ADMIN],
          moduleName: "site-setting",

        },
        // {
        //   title: "Site Setting (new)",
        //   url: "/setting-new",
        //   icon: "settings",
        //   // roles: [ROLES.ADMIN],
        // },
        {
          title: "Activity Logs",
          url: "/activity-logs",
          icon: "receipt_long",
          // roles: [ROLES.ADMIN],
          moduleName: "activity-logs",

        },
        {
          title: "Logs",
          url: "/logs",
          icon: "article",
          // roles: [ROLES.ADMIN],
          moduleName: "logs",

        },
      ],
      allCorporateNavs: [
        {
          title: "Corporate Dashboard",
          url: "/corporate/dashboard",
          icon: "dashboard",
          // roles: [ROLES.CORPORATE],
          moduleName: "corporate-dashboard",
        },
        {
          title: "Tickets",
          url: "/corporate/tickets",
          icon: "receipt",
          // roles: [ROLES.CORPORATE],
          moduleName: "tickets",
        },
        {
          title: "Employees",
          url: "/corporate/employees",
          icon: "people",
          // roles: [ROLES.CORPORATE],
          moduleName: "employees",
        },
        {
          title: "Dispatcher",
          url: "/corporate/dispatcher",
          icon: "edit_road",
          // roles: [ROLES.CORPORATE],
          moduleName: "corporate-dispatcher",
        },
        {
          title: "Trips",
          url: "/corporate/trips",
          icon: "electric_car",
          // roles: [ROLES.CORPORATE],
          moduleName: "corporate-trips",
        },
        {
          title: "Scheduled Trips",
          url: "/corporate/scheduled-trips",
          icon: "departure_board",
          // roles: [ROLES.CORPORATE],
          moduleName: "corporate-trips",
        },
        {
          title: "Trip Searches (Passengers)",
          url: "/corporate/trip-searches",
          icon: "hail",
          // roles: [ROLES.ADMIN, ROLES.DISPATCHER, ROLES.OPERATION],
          moduleName: "corporate-trip-searches",
        },
        {
          title: "Trip Requests (Drivers)",
          url: "/corporate/trip-requests",
          icon: "swipe",
          // roles: [ROLES.ADMIN, ROLES.DISPATCHER, ROLES.OPERATION],
          moduleName: "corporate-trip-requests",
        },
      ],
      paths: [],
      date: "",
    };
  },
  watch: {
    $route(to) {

      this.filter = ""
      let pathArray = to.path.split("/");

      pathArray.shift();

      this.paths = pathArray.map((path, index) => {
        return {
          path: path,
          to: index > 0 ? "/" + pathArray[index - 1] + "/" + path : "/" + path,
          text: to.matched[index] ? to.matched[index].name : "magic",
          current:
            pathArray.length < 1 || pathArray[pathArray.length - 1] === path,
        };
      });
    }
  },
  created() {
    this.$misc.get("/date").then(({ data }) => {
      this.date = " - " + data.replace("T", " ").slice(0, 16);
    });
    setInterval(() => {
      this.$misc.get("/date").then(({ data }) => {
        this.date = " - " + data.replace("T", " ").slice(0, 16);
      });
    }, 30000);
  },
  computed: {
    // roleName() {
    //   return ROLES.resolveName(this.role);
    // },
    roles() {
      return this.$store.getters.isLoggedIn
        ? this.$store.state.auth.account.roles
        : [];
    },
    role() {
      return this.$store.getters.isLoggedIn && this.$store.getters.hasRole
        ? this.$store.state.role
        : null;
    },
    currentRole() {
      return this.$store.getters.role ? this.$store.getters.role : {}
    },
    navs() {
      return this.allNavs.filter(({ title, moduleName }) => {
        const permissions = this.currentRole.permissions[moduleName]
        return title.toLowerCase().includes(this.filter.toLowerCase()) && permissions && permissions.canAccess
      });
    },
    corporateRoles() {
      return this.$store.getters.isLoggedIn
        ? this.$store.state.auth.account.corporateRoles
        : [];
    },
    corporateRole() {
      return this.$store.getters.isLoggedIn && this.$store.getters.hasCorporateRole
        ? this.$store.state.corporateRole
        : null;
    },
    currentCorporateRole() {
      return this.$store.getters.corporateRole ? this.$store.getters.corporateRole : {}
    },
    corporateNavs() {
      return this.allCorporateNavs.filter(({ title, moduleName }) => {
        const permissions = this.currentCorporateRole.permissions[moduleName]
        return title.toLowerCase().includes(this.filter.toLowerCase()) && permissions && permissions.canAccess
      });
    },
    groupedNavs() {
      return this.groupNavs(this.navs);
    },
    groupedCorporateNavs() {
      return this.groupNavs(this.corporateNavs);
    },
    sidebarGroups() {
      return this.corporateRole ? this.groupedCorporateNavs : this.groupedNavs;
    },
    hasNavs() {
      return (this.corporateRole ? this.corporateNavs : this.navs).length > 0;
    },
    name() {
      if (!this.$store.getters.isLoggedIn) return null;

      const auth = this.$store.state.auth.account;
      return `${auth.firstName} ${auth.lastName}`;
    },
    initials() {
      if (!this.name) return "";
      return this.name
        .split(" ")
        .filter(Boolean)
        .map((part) => part[0])
        .slice(0, 2)
        .join("")
        .toUpperCase();
    },
  },
  methods: {
    logout() {
      this.$store.dispatch("logout").finally(() => this.$router.push("/login"));
    },
    changeRole() {
      this.$router.push("/role");
    },
    groupNavs(navs) {
      const groups = Object.keys(NAV_GROUPS)
        .map((name) => ({
          name,
          items: navs.filter((nav) => NAV_GROUPS[name].includes(nav.moduleName)),
        }))
        .filter((group) => group.items.length);

      const known = [].concat(...Object.values(NAV_GROUPS));
      const ungrouped = navs.filter((nav) => !known.includes(nav.moduleName));
      if (ungrouped.length) groups.push({ name: "Other", items: ungrouped });

      return groups;
    },
  },
};
</script>

<style>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
  opacity: 0;
}

::-webkit-scrollbar {
  width: 12px;
  height: 16px;
}

/* Track */
::-webkit-scrollbar-track {
  border-radius: 100vh;
  background: #edf2f7;
}

/* Handle */
::-webkit-scrollbar-thumb {
  background: #cbd5e0;
  border-radius: 100vh;
  border: 3px solid #edf2f7;
}

/* Handle on hover */
::-webkit-scrollbar-thumb:hover {
  background: #a0aec0;
}

/* ── Sidebar (light theme, gold accents) ─────────────────── */
.sidebar {
  max-width: 18.5rem;
  background: linear-gradient(180deg, #ffffff 0%, #fffbf2 100%);
  border-right: 1px solid #e2e8f0;
}

.brand-logo {
  border-radius: 9999px;
  box-shadow: 0 0 0 2px rgba(255, 215, 0, 0.55),
    0 4px 10px rgba(77, 50, 0, 0.12);
}

.role-badge {
  padding-top: 1px;
  padding-bottom: 1px;
  color: #996300;
  background: rgba(255, 215, 0, 0.16);
  border: 1px solid rgba(255, 215, 0, 0.45);
}

/* Search field */
.sidebar-search {
  color: #2d3748;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}
.sidebar-search::placeholder {
  color: #a0aec0;
}
.sidebar-search:hover {
  border-color: #cbd5e0;
}
.sidebar-search:focus {
  border-color: #ffd700;
  box-shadow: 0 0 0 3px rgba(255, 215, 0, 0.25);
}
.search-icon {
  color: #a0aec0;
  transition: color 0.2s ease;
}
.sidebar-search:focus ~ div .search-icon {
  color: #996300;
}
.search-clear {
  color: #a0aec0;
  transition: color 0.15s ease;
}
.search-clear:hover {
  color: #4a5568;
}

/* Nav groups & links */
.side-group-label {
  color: #a0aec0;
}
.side-link {
  color: #4a5568;
  transition: background-color 0.15s ease, color 0.15s ease;
}
.side-link .side-link-icon {
  color: #a0aec0;
  transition: color 0.15s ease;
}
.side-link:hover {
  background: #f7fafc;
  color: #1a202c;
}
.side-link:hover .side-link-icon {
  color: #718096;
}
.side-link--active,
.side-link--active:hover {
  background: linear-gradient(
    90deg,
    rgba(255, 215, 0, 0.22),
    rgba(255, 215, 0, 0.07)
  );
  color: #996300;
  font-weight: 600;
}
.side-link--active .side-link-icon,
.side-link--active:hover .side-link-icon {
  color: #996300;
}
.side-link-indicator {
  background: #ffd700;
  box-shadow: 0 0 6px rgba(255, 215, 0, 0.7);
}

.sidebar-empty {
  color: #a0aec0;
}

.sidebar-footer {
  color: #718096;
  background: rgba(255, 255, 255, 0.6);
  border-top: 1px solid #e2e8f0;
}

/* Slim scrollbar inside the sidebar nav */
.sidebar-scroll::-webkit-scrollbar {
  width: 6px;
}
.sidebar-scroll::-webkit-scrollbar-track {
  background: transparent;
}
.sidebar-scroll::-webkit-scrollbar-thumb {
  background: #cbd5e0;
  border: none;
  border-radius: 100vh;
}
.sidebar-scroll::-webkit-scrollbar-thumb:hover {
  background: #a0aec0;
}
</style>